/*
 * Client-side mirror of the server's `ReCaptchaProtectedPathsFilter` path
 * matching rules. Used by the axios request interceptor (via
 * `reCaptchaService.ts`) to decide whether an outbound open-service-proxy
 * request needs an `X-Recaptcha-Token` header.
 *
 * The set of protected paths is CMS-driven (`FrontendConfigurationBlock.
 * reCaptchaProtectedPaths` on the Optimizely Content Delivery API) and must
 * never be hardcoded here. This module only implements the matching rules.
 *
 * Matching rules (must exactly mirror the server):
 *   - Case-insensitive.
 *   - Compare against the URI segment after `/api/{version}/openserviceproxy/`
 *     with leading and trailing slashes trimmed.
 *   - Plain-prefix entries (no `*`): match when the request path equals the
 *     entry OR continues with a `/` segment boundary. So `insurance/car`
 *     matches `insurance/car` and `insurance/car/quotes`, but NOT
 *     `insurance/carrental`.
 *   - Glob entries:
 *       `*`  matches exactly one URI segment.
 *       `**` matches zero or more URI segments.
 *     Glob entries are matched against the full path (anchored ^...$).
 *
 * Framework-agnostic. Pure functions. No side effects.
 */

/**
 * Returns `true` when `path` matches any of `patterns` under the rules above.
 * Returns `false` when `patterns` is missing or empty (nothing is protected).
 */
export function isProtectedPath(path: string, patterns: readonly string[] | undefined): boolean {
	if (!patterns || patterns.length === 0) {
		return false;
	}
	const normalizedPath = normalize(path);
	for (const pattern of patterns) {
		const normalizedPattern = normalize(pattern);
		if (normalizedPattern.length === 0) {
			continue;
		}
		if (matchesPattern(normalizedPath, normalizedPattern)) {
			return true;
		}
	}
	return false;
}

/**
 * Extracts the sub-path a URL targets under the open-service proxy, i.e. the
 * substring after `/openserviceproxy/`, with any query string and leading /
 * trailing slashes removed. Returns `undefined` when the URL does not target
 * the open-service proxy.
 */
export function extractOpenProxySubPath(url: string | undefined): string | undefined {
	if (!url) {
		return undefined;
	}
	const marker = '/openserviceproxy/';
	const idx = url.toLowerCase().indexOf(marker);
	if (idx === -1) {
		return undefined;
	}
	let rest = url.slice(idx + marker.length);
	const q = rest.indexOf('?');
	if (q !== -1) {
		rest = rest.slice(0, q);
	}
	return normalize(rest);
}

function normalize(value: string | undefined): string {
	if (!value) {
		return '';
	}
	let out = value.trim().toLowerCase();
	while (out.startsWith('/')) {
		out = out.slice(1);
	}
	while (out.endsWith('/')) {
		out = out.slice(0, -1);
	}
	return out;
}

function matchesPattern(path: string, pattern: string): boolean {
	if (pattern.includes('*')) {
		return matchGlob(path.split('/'), pattern.split('/'));
	}
	// Plain prefix — equal or continues at a segment boundary.
	return path === pattern || path.startsWith(pattern + '/');
}

function matchGlob(pathSegs: readonly string[], patternSegs: readonly string[]): boolean {
	if (patternSegs.length === 0) {
		return pathSegs.length === 0;
	}
	const head = patternSegs[0];
	const restPattern = patternSegs.slice(1);

	if (head === '**') {
		// `**` consumes zero or more path segments; try every split.
		for (let i = 0; i <= pathSegs.length; i++) {
			if (matchGlob(pathSegs.slice(i), restPattern)) {
				return true;
			}
		}
		return false;
	}

	if (pathSegs.length === 0) {
		return false;
	}
	const pathHead = pathSegs[0];
	if (head === '*' || head === pathHead) {
		return matchGlob(pathSegs.slice(1), restPattern);
	}
	return false;
}
